import Button from 'components/ui/Button';
import classNames from 'classnames';
import Drawer from 'components/Drawer';
import Icons from 'components/icons';
import RecipeGraph from 'compositions/RecipeGraph';
import Text from 'components/ui/Text';
import { FC, useEffect, useRef, useState } from 'react';
import { selectSelectedRoomOptions } from 'store/roomSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useSwipeDetect } from 'hooks';
import { useTranslation } from 'hooks/useTranslation';
import { useGoogleTagManager } from '@grace-studio/graceful-next/hooks';
import { selectUiState, setUi } from 'store/uiSlice';

type RecipeValueVisualizationProps = {
  onOpenRecipeClick: () => void;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
};

const RecipeValueVisualization: FC<RecipeValueVisualizationProps> = ({
  onOpenRecipeClick,
  isOpen,
  onClose,
  onOpen,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const translation = useTranslation();
  const gtm = useGoogleTagManager();
  const currentRoomOptions = useAppSelector(selectSelectedRoomOptions);
  const canOpenRecipe =
    currentRoomOptions?.lightingSolution?.slug?.current !== 'none-daylight';
  const [hasClosedColor, setHasClosedColor] = useState(true);
  const dispatch = useAppDispatch();
  const ui = useAppSelector(selectUiState) || {};

  useSwipeDetect({
    element: panelRef.current,
    configuration: {
      direction: 'right',
    },
    onSwipe: onClose,
  });

  useEffect(() => {
    if (isOpen) {
      setHasClosedColor(false);
    }

    if (!isOpen && !hasClosedColor) {
      setTimeout(() => {
        setHasClosedColor(true);
      }, 250);
    }

    dispatch(
      setUi({
        ...ui,
        rightPanelOpen: isOpen,
      }),
    );
  }, [hasClosedColor, isOpen]);
  useEffect(() => {
    dispatch(
      setUi({
        ...ui,
        canOpenRecipe: canOpenRecipe,
      }),
    );
  }, [canOpenRecipe]);

  const bgColorClasses = hasClosedColor
    ? 'bg-blue text-white'
    : 'bg-white text-blue';

  const bgClasses = classNames(
    'transition pt-4 lg:pt-8 pb-8 px-6 md:px-8 xl:px-12',
    'bg-white text-blue overflow-auto max-h-[75vh]',
  );
  const notchClasses = classNames(
    'flex items-center justify-center transition-all h-36 rounded-l-lg text-[12px] translate-x-[2px] relative',
    isOpen ? 'w-8' : 'w-[78px]',
    bgColorClasses,
  );
  const closeButtonClasses = classNames(
    'transition',
    hasClosedColor ? 'text-white' : 'text-blue',
    isOpen ? '' : 'rotate-180',
  );
  const bgFillClasses = classNames(
    'absolute top-0 bottom-0 right-0 translate-x-full w-80',
    bgColorClasses,
  );

  return (
    <Drawer
      id="recipe-drawer"
      ref={panelRef}
      disableBackdrop
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      speed="slow"
      position="right"
      hotKey="R"
      className="top-auto bottom-8 lg:bottom-12 z-20"
    >
      <div className="absolute top-0 left-0 -translate-x-full">
        <button
          aria-label="Toggle visualization"
          onClick={() => (isOpen ? onClose() : onOpen())}
          className={notchClasses}
        >
          <Icons.Chevron className={closeButtonClasses} />
          <span
            className={classNames(
              'sabsolute -rotate-90 whitespace-nowrap translate-x-1 -mx-4 font-bold text-medium',
              isOpen ? 'hidden' : 'flex',
            )}
          >
            {translation('recipe.myRecipe')}
          </span>
        </button>
      </div>

      <div className={bgFillClasses}></div>

      <div className={bgClasses}>
        <Text
          bold
          center
          elementType="div"
          color="grey-500"
          className="small-caps"
        >
          {translation('recipe.myRecipe')}
        </Text>
        <div className="grid gap-4 w-[208px] md:w-[300px] lg:w-[400px]">
          <RecipeGraph summary />
        </div>
      </div>
      <div
        id="show-recipe-button"
        className="flex justify-end bg-grey-200 py-2 xl:py-6 px-6 md:px-8 rounded-bl-lg"
      >
        <Button
          disabled={!canOpenRecipe}
          style="onlyText"
          onClick={() => {
            onOpenRecipeClick();
            gtm.trackEvent({ event: 'btn_open_recipe' });
          }}
          className="!w-full !justify-end !h-8"
        >
          {translation('recipe.fullRecipe')}{' '}
          <Icons.Arrow className="text-large w-[1em] ml-2" />
        </Button>
      </div>
    </Drawer>
  );
};

export default RecipeValueVisualization;
